import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";
import { GeometryEditorMode, geometryModeSet, Positions } from "../../ducks/geometryEditor/geometryEditor";
import { getGeometryEditor } from "../../ducks/geometryEditor/selectors";
import { resetMouseLabel } from "../../ducks/mouseLabel/mouseLabel";
import {
  setEditFeatureGeometry,
  subtractEditFeatureGeometry,
  unitEditFeatureGeometry,
} from "../../ducks/editFeature/editFeature";
import { useFeatureEditHistory } from "../../modules/FeatureCard/hooks/useFeatureEditHistory";
import { useLayerCrs } from "../../hooks/layer/useLayerCrs";
import { useAction } from "../../hooks/useAction";

export const useGeometryEditor = () => {
  const { geometryType, mode, layerName, active } = useSelector(getGeometryEditor);
  const [crs, { loading }] = useLayerCrs(layerName);
  const [{ geometry }] = useFeatureEditHistory();
  const dispatch = useDispatch();

  const editGeometry = useAction(setEditFeatureGeometry);
  const onPointAdd = useAction(resetMouseLabel);
  const setGeometryMode = useAction(geometryModeSet);

  const handleEditGeometry = useCallback(
    (positions: Positions) => {
      switch (mode) {
        case GeometryEditorMode.Expand: {
          editGeometry([...(geometry || []), ...positions] as Positions);
          setGeometryMode(GeometryEditorMode.Edit);
          break;
        }
        case GeometryEditorMode.Unite: {
          setGeometryMode(GeometryEditorMode.Edit);
          dispatch(unitEditFeatureGeometry(positions));
          break;
        }
        case GeometryEditorMode.Subtract: {
          setGeometryMode(GeometryEditorMode.Edit);
          dispatch(subtractEditFeatureGeometry(positions));
          break;
        }
        default: {
          editGeometry(positions);
          break;
        }
      }
    },
    [mode, editGeometry, geometry, setGeometryMode, dispatch],
  );

  return [
    { geometryType, layerName, geometry, mode, crs, loading, active },
    { editGeometry: handleEditGeometry, onPointAdd },
  ] as const;
};
